import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { DataTable, DataTableColumn } from "@/components/ui/data-table";
import { usePeopleStore } from "./peopleStore";
import { canEditOwnedEntity, Permission, Person } from "@forgepoint/shared";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { usePaginatedEntitySearch } from "@/lib/usePaginatedEntitySearch";
import { DeleteConfirmationDialog } from "@/components/DeleteConfirmationDialog";
import {
  useAuthStore,
  useHasAnyPermission,
  useHasPermission,
} from "@/store/authStore";
import { toast } from "sonner";
import PersonCreateSheet from "./PersonCreateSheet";
import PersonDetailSheet from "./PersonDetailSheet";

/**
 * Controller component for the People list view.
 * Manages the data table, search, delete dialog, and delegates
 * the create workflow to PersonCreateSheet.
 * @returns The PeopleController component
 */
export default function PeopleController() {
  const { people, listLoading, listError, fetchPeople, deletePerson } =
    usePeopleStore();
  const canEditAllPeople = useHasPermission(Permission.PEOPLE_EDIT_ALL);
  const canEditOwnPeople = useHasPermission(Permission.PEOPLE_EDIT_OWN);
  const canCreatePeople = useHasAnyPermission(
    Permission.PEOPLE_EDIT_ALL,
    Permission.PEOPLE_EDIT_OWN,
  );
  const currentUserId = useAuthStore((state) => state.user?.id);

  /**
   * Whether the current user may edit (and delete) a specific person. People
   * are owned by the sales rep of their company, hydrated onto the person as
   * companySalesRepUserId.
   * @param person - The person to check
   * @returns True when the user holds edit.all, or edit.own and owns the person's company
   */
  const canEditPerson = (person: Person): boolean =>
    canEditOwnedEntity(
      canEditAllPeople,
      canEditOwnPeople,
      !!person.companySalesRepUserId &&
        person.companySalesRepUserId === currentUserId,
    );

  const {
    entitiesArray: peopleArray,
    handleSearch,
    setDisplayedEntityIds: setDisplayedPersonIds,
    refetchAfterRemoval,
    pagination,
  } = usePaginatedEntitySearch({
    entities: people,
    fetchEntities: fetchPeople,
  });

  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);

  const navigate = useNavigate();
  const { personId: detailPersonId } = useParams<{ personId?: string }>();

  /**
   * Checks whether a person is synced from an external system.
   * @param person - The person to check, or null
   * @returns True if the person has external mappings
   */
  const isPersonSynced = (person: Person | null): boolean =>
    Boolean(person && person.externalMappings.length > 0);

  const columns: DataTableColumn<Person>[] = [
    {
      key: "firstName",
      header: "First Name",
      type: "string",
    },
    {
      key: "lastName",
      header: "Last Name",
      type: "string",
    },
    {
      key: "email",
      header: "Email",
      type: "string",
      className: "hidden md:table-cell",
    },
    {
      key: "companyId",
      header: "Company",
      type: "string",
      width: 200,
      className: "hidden lg:table-cell",
      format: (_value: unknown, person: Person) => person.companyName ?? "-",
    },
    {
      key: "isPrimaryCompanyContact",
      header: "Primary",
      type: "string",
      width: 110,
      className: "hidden lg:table-cell",
      format: (_value: unknown, person: Person) =>
        person.isPrimaryCompanyContact ? (
          <Badge variant="outline">Primary</Badge>
        ) : (
          "-"
        ),
    },
    {
      key: "createdAt",
      header: "Created",
      type: "date",
      className: "hidden sm:table-cell",
    },
    {
      key: "syncStatus",
      header: "Sync",
      width: 90,
      type: "string",
      className: "hidden md:table-cell",
      format: (_value: unknown, person: Person) =>
        isPersonSynced(person) ? (
          <Badge variant="secondary">Synced</Badge>
        ) : (
          "-"
        ),
    },
    {
      key: "actions",
      header: "",
      width: 50,
      format: (_value: unknown, person: Person) => {
        if (!canEditPerson(person)) {
          return null;
        }
        const synced = isPersonSynced(person);
        return (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(person);
            }}
            disabled={synced}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        );
      },
    },
  ];

  const handleCreate = () => {
    setIsCreateSheetOpen(true);
  };

  const handleDelete = (person: Person) => {
    if (isPersonSynced(person)) {
      toast.info(
        "Synced people cannot be deleted. Manage this record in the source system.",
      );
      return;
    }
    setSelectedPerson(person);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedPerson) {
      return;
    }

    if (isPersonSynced(selectedPerson)) {
      toast.info(
        "Synced people cannot be deleted. Manage this record in the source system.",
      );
      setIsDeleteDialogOpen(false);
      setSelectedPerson(null);
      return;
    }

    try {
      const success = await deletePerson(selectedPerson.id);
      if (success) {
        toast.success("Person deleted successfully");
        await refetchAfterRemoval();
      } else {
        toast.error("Failed to delete person");
      }
    } catch (_error) {
      toast.error("Failed to delete person");
    } finally {
      setIsDeleteDialogOpen(false);
      setSelectedPerson(null);
    }
  };

  /**
   * Callback invoked after a person is successfully created.
   * @param person - The newly created person
   */
  const handleCreateSaved = (person: Person) => {
    setDisplayedPersonIds((prev) => [person.id, ...prev]);
    setIsCreateSheetOpen(false);
  };

  if (listError) {
    return (
      <div className="p-8 text-red-600">Error loading people: {listError}</div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <DataTable
        columns={columns}
        data={peopleArray}
        onClickRow={(person) => navigate(`/people/${person.id}`)}
        onChangeSearch={handleSearch}
        searchPlaceholder="Search people..."
        className="h-full"
        isLoading={listLoading && peopleArray.length === 0}
        pagination={pagination}
        onCreate={canCreatePeople ? handleCreate : undefined}
        createButtonText="New"
      />

      <PersonCreateSheet
        open={isCreateSheetOpen}
        onOpenChange={setIsCreateSheetOpen}
        onSaved={handleCreateSaved}
      />

      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        entityType="Person"
        itemName={
          selectedPerson
            ? `${selectedPerson.firstName} ${selectedPerson.lastName}`
            : undefined
        }
      />

      {detailPersonId && (
        <PersonDetailSheet
          open={!!detailPersonId}
          onOpenChange={(open) => {
            if (!open) {
              navigate("/people");
            }
          }}
          personId={detailPersonId}
        />
      )}
    </div>
  );
}
